import { ALL_DEPARTMENTS_QUERY, type DepartmentNode } from '@/modules/companies'
import { useNodes } from '@/utils/hooks/useNodes'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { ComboboxCommonProps, ComboboxItem } from './combobox'
import { Combobox } from './combobox'

type DepartmentOption = Pick<DepartmentNode, 'id' | 'name'> &
  Partial<Omit<DepartmentNode, '__typename'>>

interface CommonProps extends ComboboxCommonProps<string> {
  alwaysInclude?: DepartmentOption | null
  exclude?: string[]
  /** Restricts the options to these departments when non-empty. */
  dataset?: DepartmentOption[]
}

interface SingleProps extends CommonProps {
  mode: 'single'
  value: string
  clearable?: boolean
  onChange(value: string): void
}

interface MultiProps extends CommonProps {
  mode: 'multiple'
  value: string[]
  clearable?: false
  onChange(value: string[]): void
}

type Props = SingleProps | MultiProps

export function DepartmentCombobox({
  mode,
  value,

  alwaysInclude = null,
  exclude = [],
  dataset,
  clearable = false,

  placeholder,
  searchPlaceholder,
  noResultsText,

  onChange,

  ...props
}: Props) {
  const translations = useTranslate({
    single: {
      placeholder: 'components.combobox.department.placeholder',
      search: 'common.search',
      noResults: 'common.no-results',
    },
    multiple: {
      placeholder: 'components.multi-combobox.department.placeholder',
      search: 'common.search',
      noResults: 'common.no-results',
    },
  })

  const { data: departmentsData } = useQuery(ALL_DEPARTMENTS_QUERY)

  const allDepartments = useNodes(departmentsData?.allDepartments)
  const departments = dataset && dataset.length > 0 ? dataset : allDepartments

  const departmentItems: ComboboxItem[] = (() => {
    const items = departments
      .filter(
        department =>
          department.id !== alwaysInclude?.id &&
          !exclude.includes(department.id)
      )
      .map<ComboboxItem>(department => ({
        value: department.id,
        label: department.name,
      }))

    if (!!alwaysInclude)
      items.unshift({
        value: alwaysInclude.id,
        label: alwaysInclude.name,
      })

    return items
  })()

  if (mode === 'single')
    return (
      <Combobox
        {...props}
        mode="single"
        value={value}
        items={departmentItems}
        clearable={clearable}
        placeholder={placeholder ?? translations.single.placeholder}
        searchPlaceholder={searchPlaceholder ?? translations.single.search}
        noResultsText={noResultsText ?? translations.single.noResults}
        onChange={onChange}
      />
    )

  return (
    <Combobox
      {...props}
      mode="multiple"
      value={value}
      items={departmentItems}
      placeholder={placeholder ?? translations.multiple.placeholder}
      searchPlaceholder={searchPlaceholder ?? translations.multiple.search}
      noResultsText={noResultsText ?? translations.multiple.noResults}
      onChange={onChange}
    />
  )
}
